<template>
    <div>
        <main
            :class="{ 'api-template': fallback }"
            :data-legacy-product-sku="story?.content?.product_id"
            :data-propel-product-sku="story?.content?.propelProductSKU"
        >
            <component :is="story?.content?.component" v-if="story" :blok="story?.content" />
        </main>
    </div>
</template>

<script setup lang="ts">
import type { IL4ApiProductFull } from '~/types/components/L4Products';
import removeAccents from 'remove-accents';
import { replaceRootHeaderAndFooter } from '~/utils/sbHelper';
const { locale, locales, t } = useI18n();

const route: any = useRoute();
const fallback = ref(false);

// Constant variable for access to Runtime Config
const config = useRuntimeConfig();

// FEEFO API
const { feefoMerchantId } = useFeefo();

const { data: feefoData }: any = await useAsyncData(
    'feefo_' + feefoMerchantId + '_' + route.params.product_id,
    async () => {
        await $fetch(
            `https://api.feefo.com/api/20/reviews/summary/product?merchant_identifier=${feefoMerchantId}&product_sku=${route.params.product_id}&since_period=All&unanswered_feedback=include`
        );
    }
);

if (feefoData.value) {
    const feefoSchema = `{
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": "${t('reviewSchema.name')}",
    "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": ${feefoData.value?.rating?.rating},
        "bestRating": ${feefoData.value?.rating?.max},
        "reviewCount": ${feefoData.value?.meta?.count}
    }
}`;

    useHead({
        script: [{ type: 'application/ld+json', innerHTML: feefoSchema }],
    });
}

// Need to resolve shared components to allow the storyblok API
// to expose this for us to use.
const resolveRelations: string[] = [
    'L4ProductPage.airport',
    'L4FooterContainer.footer',
    'L4HeaderContainer.header',
    'L4QuoteFormExtended.airport',
    'L4QuoteFormHotels.airport',
    'L4QuoteFormExtended.keysellingpoints',
    'L4BlockUSP.block',
    'L4CustomerReviews.reviewsBlock',
    'L4QuoteForm.airport',
    'L4Testimonial.review_what',
    'L4ProductHeader.product',
    'L4ProductHeader.airport',
    'L4ProductGrid.products',
    'L4Map.products',
    'L4Map.airport',
    'L4ProductGrid.products',
    'L4TrustedPartners.partners',
    'L4ProductGrid.products',
    'L4ProductMap.products',
    'L4ProductMap.airport',
    'L4AirportParkingTabs.airport',
    'L4ProductPricingTable.carParks',
    'L4CarParkingOptions.products',
    'L4ProductPricingTable.carParks',
    'L4CarParkingOptions.airport',
    'L4TopRatedCarParks.carParks',
    'L4TransferOptionsContainer.options_component',
    'L4EmailSignupNew.signup',
];

const { data: apiProduct } = await useAsyncData<IL4ApiProductFull>(
    'product',
    () =>
        $privateApi<IL4ApiProductFull>(`parking/products/public/${route.params.product_id}`, {
            method: 'GET',
            params: {
                lang: getLanguage(),
            },
        }),
    { lazy: true }
);

provide('apiProduct', apiProduct);

if (!apiProduct.value) {
    navigateTo(route.path.replace(`${route.params.product}/${route.params.product_id}`, ''), {
        external: true,
        redirectCode: 301,
    });
}

const routePath = route.path.includes(`${route.params.product}/${route.params.product_id}`)
    ? route.path.replace(`${route.params.product}/${route.params.product_id}`, `${route.params.product_id}`)
    : route.path;

const sbLocation = `/${config?.public.STORYBLOK_FOLDER}/pages${routePath}`;

// Perform request to Storyblok to retrieve content
const story: any = await fetchStory(
    sbLocation,
    (data) => {
        if (!data.value) {
            const sbDefaultProductTemplate = `/${config?.public.STORYBLOK_FOLDER}/pages/product-template`;
            fallback.value = true;
            return fetchStory(sbDefaultProductTemplate, successFetchStory, defaultErrorCallback);
        }

        return successFetchStory(data);
    },
    (err: any) => {
        if (err.status !== 404) {
            return defaultErrorCallback(err);
        }
        const sbDefaultProductTemplate = `/${config?.public.STORYBLOK_FOLDER}/pages/product-template`;
        fallback.value = true;
        return fetchStory(sbDefaultProductTemplate, successFetchStory, defaultErrorCallback);
    }
);

provide('propelProductSKU', story?.value?.content?.propelProductSKU || '');

function defaultErrorCallback(err) {
    if (route.path === '/robots.txt') return;
    console.error('path:', route.fullPath);
    console.error(err);
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

async function successFetchStory(data) {
    const pageName = convertTitleToSlug(data.value.name);
    const name = convertTitleToSlug(apiProduct.value.displayName);

    if (
        (name !== route.params.product && pageName !== 'product-template') ||
        (apiProduct.value && convertTitleToSlug(apiProduct.value.displayName) !== route.params.product)
    ) {
        console.error('redirect to right slug:', route.path.replace(route.params.product, name));
        return navigateTo(route.path.replace(route.params.product, name), {
            external: true,
            redirectCode: 301,
        });
    }

    if (pageName === 'product-template' && !apiProduct.value) {
        throw createError({
            statusCode: 404,
            statusMessage: 'Page Not Found',
            fatal: true,
        });
    }

    setSeoMeta(data.value?.content?.seo, config?.public, route);
    if (pageName === 'product-template') {
        useHead({
            title: apiProduct.value.displayName,
        });

        data.value = await getRootHeaderAndFooter(data.value);
    }

    return data;
}

async function fetchStory(sbLocation: string, successCallback: any, errorCallback: any) {
    return await useAsyncStoryblok(sbLocation, {
        version: config?.public?.STORYBLOK_VERSION === 'published' ? 'published' : 'draft',
        language: getLanguage(),
        resolve_relations: resolveRelations,
    })
        .then(successCallback)
        .catch(errorCallback);
}

// params: pageContent - original page content from SB
// returns page content with header&footer from root page
async function getRootHeaderAndFooter(pageContent: any) {
    const decodedPath = decodeURI(route.path);
    const productLocationPath = decodedPath
        .replace(`/${route.params.product}/${route.params.product_id}`, '')
        .replace(' ', '-');
    const sbProductLocationPath = `/${config?.public.STORYBLOK_FOLDER}/pages${productLocationPath}`;

    return await useAsyncStoryblok(sbProductLocationPath, {
        version:
            config?.public?.STORYBLOK_VERSION === 'published'
                ? ('published' as 'published' | 'draft')
                : ('draft' as 'published' | 'draft'),
        language: getLanguage(),
        resolve_relations: resolveRelations,
    })
        .then((data) => {
            const storyData = data?.value || {};
            return replaceRootHeaderAndFooter(storyData, pageContent);
        })
        .catch((err) => defaultErrorCallback(err));
}

function getStoryblokBlockedRoutes() {
    if (config?.public.STORYBLOK_ROUTE_BLOCKED) {
        const blocked = config?.public.STORYBLOK_ROUTE_BLOCKED.split(', ');
        return blocked.some((block) => route.fullPath.includes(block));
    } else {
        return false;
    }
}

function convertTitleToSlug(title: string): string {
    const noAccents = removeAccents(title);
    return noAccents
        .toLowerCase()
        .replaceAll(/[^a-zA-Z0-9\-\s/]+/g, '')
        .replaceAll(' ', '-')
        .replaceAll('/', '-')
        .replaceAll(/[\s-]+\s*/g, '-')
        .replaceAll(/-+/g, '-')
        .replaceAll(/[\s\-/]+$/g, '')
        .replaceAll(/^[\s-]+/g, '');
}

function getLanguage() {
    const routePath = route.path?.startsWith('/') ? route.path.slice(1) : route.path;
    const location = routePath.split('/')[0];

    return location.length > 2
        ? location.split('-')[1]
        : locales.value.filter((e) => e.code === locale.value)[0].storyblok;
}

// Send user to error page if no story found
if (getStoryblokBlockedRoutes() || !story.value) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

if (!import.meta.server) {
    // Allow use of Storyblok Bridge (Visual Editor)
    useStoryblokBridge(story.id, (evStory: any) => (story.value = evStory), { resolveRelations });
}
</script>
